import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';

// Analytics event tracking utility
export class Analytics {
  // Authentication Events
  static loginAttempt(email: string) {
    this.log('login_attempt', {
      method: 'email',
      email_domain: email.split('@')[1] // Track domain for privacy
    });
  }

  static loginSuccess(email: string) {
    this.log('login_success', {
      method: 'email',
      email_domain: email.split('@')[1]
    });
  }

  static loginError(errorCode: string, email: string) {
    this.log('login_error', {
      error_code: errorCode,
      method: 'email',
      email_domain: email.split('@')[1]
    });
  }

  static registerAttempt(email: string) {
    this.log('register_attempt', {
      method: 'email',
      email_domain: email.split('@')[1]
    });
  }

  static registerSuccess(email: string) {
    this.log('register_success', {
      method: 'email',
      email_domain: email.split('@')[1]
    });
  }

  static registerError(errorCode: string, email: string) {
    this.log('register_error', {
      error_code: errorCode,
      method: 'email',
      email_domain: email.split('@')[1]
    });
  }

  static logout() {
    this.log('logout');
  }

  // Navigation Events
  static navigationChange(from: string, to: string) {
    this.log('navigation_change', {
      from_page: from,
      to_page: to
    });
  }

  static languageChange(fromLang: string, toLang: string) {
    this.log('language_change', {
      from_language: fromLang,
      to_language: toLang
    });
  }

  // Verification Events
  static verificationStarted(type: 'vat' | 'peppol' | 'dual', method: 'single' | 'batch') {
    this.log('verification_started', {
      verification_type: type,
      verification_method: method
    });
  }

  static verificationSuccess(type: 'vat' | 'peppol' | 'dual', method: 'single' | 'batch', resultCount?: number) {
    this.log('verification_success', {
      verification_type: type,
      verification_method: method,
      result_count: resultCount
    });
  }

  static verificationError(type: 'vat' | 'peppol' | 'dual', method: 'single' | 'batch', errorCode: string) {
    this.log('verification_error', {
      verification_type: type,
      verification_method: method,
      error_code: errorCode
    });
  }

  // Single Verification Events
  static singleVerificationInput(type: 'vat' | 'peppol' | 'dual') {
    this.log('single_verification_input', {
      verification_type: type
    });
  }

  static singleVerificationSubmit(type: 'vat' | 'peppol' | 'dual', identifier: string) {
    this.log('single_verification_submit', {
      verification_type: type,
      identifier_type: this.getIdentifierType(identifier)
    });
  }

  // Batch Verification Events
  static batchVerificationUpload(type: 'vat' | 'peppol' | 'dual', fileType: string, fileSize: number) {
    this.log('batch_verification_upload', {
      verification_type: type,
      file_type: fileType,
      file_size: fileSize
    });
  }

  static batchVerificationTextInput(type: 'vat' | 'peppol' | 'dual', identifierCount: number) {
    this.log('batch_verification_text_input', {
      verification_type: type,
      identifier_count: identifierCount
    });
  }

  static batchVerificationProcess(type: 'vat' | 'peppol' | 'dual', identifierCount: number) {
    this.log('batch_verification_process', {
      verification_type: type,
      identifier_count: identifierCount
    });
  }

  static batchVerificationComplete(type: 'vat' | 'peppol' | 'dual', totalProcessed: number, validCount: number, invalidCount: number, errorCount: number) {
    this.log('batch_verification_complete', {
      verification_type: type,
      total_processed: totalProcessed,
      valid_count: validCount,
      invalid_count: invalidCount,
      error_count: errorCount
    });
  }

  // Export Events
  static exportResults(type: 'vat' | 'peppol' | 'dual', method: 'single' | 'batch', format: 'csv' | 'json') {
    this.log('export_results', {
      verification_type: type,
      verification_method: method,
      export_format: format
    });
  }

  // Error Events
  static generalError(errorType: string, errorMessage: string, context?: string) {
    this.log('general_error', {
      error_type: errorType,
      error_message: errorMessage,
      context: context
    });
  }

  static apiError(service: 'vat' | 'peppol' | 'dual', errorCode: string, errorMessage: string) {
    this.log('api_error', {
      service: service,
      error_code: errorCode,
      error_message: errorMessage
    });
  }

  static networkError(service: 'vat' | 'peppol' | 'dual') {
    this.log('network_error', {
      service: service
    });
  }

  // User Engagement Events
  static modalOpen(modalType: 'terms' | 'privacy' | 'about') {
    this.log('modal_open', {
      modal_type: modalType
    });
  }

  static modalClose(modalType: 'terms' | 'privacy' | 'about') {
    this.log('modal_close', {
      modal_type: modalType
    });
  }

  static helpViewed(section: string) {
    this.log('help_viewed', {
      help_section: section
    });
  }

  static downloadTemplate(type: 'vat' | 'peppol' | 'dual') {
    this.log('download_template', {
      template_type: type
    });
  }

  // Performance Events
  static pageLoad(page: string, loadTime: number) {
    this.log('page_load', {
      page: page,
      load_time_ms: loadTime
    });
  }

  static apiResponseTime(service: 'vat' | 'peppol' | 'dual', responseTime: number) {
    this.log('api_response_time', {
      service: service,
      response_time_ms: responseTime
    });
  }

  // Retry Events
  static autoRetryAttempt(service: string) {
    this.log('auto_retry_attempt', {
      service: service,
      retry_type: 'automatic'
    });
  }

  static manualRetryAttempt(service: string) {
    this.log('manual_retry_attempt', {
      service: service,
      retry_type: 'manual'
    });
  }

  static retryCancelled(service: string) {
    this.log('retry_cancelled', {
      service: service
    });
  }

  static verificationEmailSent(email: string) {
    this.log('verification_email_sent', {
      email: email
    });
  }

  // Utility Methods
  static log(eventName: string, parameters?: Record<string, any>) {
    try {
      if (analytics) {
        logEvent(analytics, eventName, parameters);
      }
    } catch (error) {
      console.warn('Analytics logging failed:', error);
    }
  }

  private static getIdentifierType(identifier: string): string {
    if (/^[A-Z]{2}[0-9]+$/.test(identifier)) {
      return 'vat_number';
    } else if (/^[0-9]{9}$/.test(identifier)) {
      return 'duns';
    } else if (/^[0-9]{13}$/.test(identifier)) {
      return 'gln';
    } else {
      return 'other';
    }
  }
}

// Performance tracking utility
export class PerformanceTracker {
  private static timers: Map<string, number> = new Map();

  static startTimer(key: string) {
    this.timers.set(key, Date.now());
  }

  static endTimer(key: string, logEvent?: string, additionalParams?: Record<string, any>) {
    const startTime = this.timers.get(key);
    if (startTime) {
      const duration = Date.now() - startTime;
      this.timers.delete(key);
      
      if (logEvent) {
        Analytics.log(logEvent, {
          duration_ms: duration,
          ...additionalParams
        });
      }
      
      return duration;
    }
    return 0;
  }
}

// Error tracking utility
export class ErrorTracker {
  static trackError(error: Error, context?: string, additionalData?: Record<string, any>) {
    Analytics.generalError(
      error.name || 'UnknownError',
      error.message,
      context
    );

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Tracked Error:', {
        error,
        context,
        additionalData
      });
    }
  }

  static trackApiError(service: 'vat' | 'peppol' | 'dual', error: any, context?: string) {
    Analytics.apiError(
      service,
      error.code || 'unknown',
      error.message || 'Unknown API error'
    );

    if (process.env.NODE_ENV === 'development') {
      console.error(`API Error (${service}):`, {
        error,
        context
      });
    }
  }
}
